/**
 * 2026-05-03 8:30AM BUG FIX: Stale invoice data when printing multiple invoices in sequence.
 *
 * PROBLEM: When PuppeteerSharp (server-side PDF renderer in PageHandler.cs) prints multiple invoices
 * back-to-back, it reuses the same browser page and navigates via Angular's in-app router
 * (window.navigateByUrl) instead of doing a full page reload. Because Angular reuses the same
 * component instance when navigating to the same route pattern (e.g., /printable-invoice/:id),
 * ngOnInit() only fires ONCE — on the first invoice. The locally computed fields (employee,
 * flatDetails, serviceDisplayName, labelApi) retained stale data from the FIRST invoice, while
 * template bindings directly to invoiceHeader properties (invoice #, dates, financial totals)
 * DID update correctly via Angular change detection. This caused the second invoice to show the
 * first invoice's employee name, line items, and shipping info, but with the correct header
 * fields and financial summary.
 *
 * FIX: Changed from OnInit to OnChanges. Moved all the computation logic into a private
 * buildFromInvoice() method that runs every time the invoiceHeader @Input() changes, ensuring
 * all computed fields are recalculated for each invoice. The old ngOnInit() code is commented
 * out below the new implementation for reference.
 */
import { Component, Input, OnChanges, SimpleChanges, ViewEncapsulation } from "@angular/core";
import { InvoiceHeader } from "app/model/entities/entity-model";
import { DbQueryService } from "app/shared/db-query.service";
import { UtilFns } from "app/shared/util-fns";
import { ShippingService } from "app/shipping/shipping.service";

/**
 * One billing leg of an InvoiceDetail. A detail carrying both a shipQty and a redistributeQty
 * prints as two rows: the New leg (Unitec goods at full price) and the Reissue leg (account-owned
 * goods, billed only a reissuance rate). Add-ons hang under the leg they were charged against, so
 * every printed row rolls up into either Subtotal New or Subtotal Reissue.
 */
interface FlatLeg {
  mfr: string;
  desc: string;
  // style: string;
  ptConfigName: string;
  featureChoicesExtract: string;
  legLabel: string;
  qty: number;
  unitAmt: string;
  /** null on Reissue legs — oversize does not apply, prints as an em-dash. */
  unitOs: string;
  total: string;
  embs: FlatEmb[];
}
interface FlatEmb {
  description: string;
  additionalInfo: string;
  qty: number;
  unitPrice: string;
  total: string;
}

@Component({
  selector: "app-printable-invoice-content",
  templateUrl: "./printable-invoice-content.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class PrintableInvoiceContentComponent implements OnChanges {
  @Input() invoiceHeader!: InvoiceHeader;

  // dueDate: Date;
  employee!: string;
  flatLegs!: FlatLeg[];
  getAddress = UtilFns.getAddress;
  serviceDisplayName!: string;
  labelApi!: string;

  constructor(private shippingService: ShippingService, private dbQueryService: DbQueryService) {}

  /** Drives whether the summary breaks the subtotal into its New and Reissue halves. */
  get hasReissue() {
    return (this.invoiceHeader?.calcExtendedReissueQty() ?? 0) > 0;
  }

  get subtotalAmt() {
    return (this.invoiceHeader.calcExtendedAmt() ?? 0) + (this.invoiceHeader.calcExtendedReissueAmt() ?? 0);
  }

  /**
   * 2026-05-03 8:30AM — Replaced ngOnInit() with ngOnChanges() so that all computed fields
   * are recalculated whenever the invoiceHeader @Input() changes. This is the core of the fix:
   * when PuppeteerSharp reuses the component instance for a second invoice, ngOnChanges fires
   * with the new invoiceHeader, and buildFromInvoice() recomputes everything.
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['invoiceHeader'] && this.invoiceHeader) {
      this.buildFromInvoice();
    }
  }

  /**
   * 2026-05-03 8:30AM — Extracted from the old ngOnInit(). This method computes all the local
   * fields (employee, flatDetails, serviceDisplayName, labelApi, modTotal) from the current
   * invoiceHeader. It runs on every @Input() change, not just the first one.
   */
  private buildFromInvoice() {
    const invoiceDetails = this.invoiceHeader.invoiceDetails;

    const fmtCurrency = UtilFns.fmtCurrency;
    const chargeAddonsOnReissuance = this.invoiceHeader.joHeader.chargeAddonsOnReissuance;

    this.flatLegs = [];
    invoiceDetails.forEach((d) => {
      const jod = d.joDetail;
      const style = jod.product.productType;
      const shipQty = d.shipQty ?? 0;
      const reissueQty = d.redistributeQty ?? 0;

      const identity = {
        mfr: style.manufacturer.name,
        desc: style.description,
        // style: style.style,
        ptConfigName: jod.productTypeConfigName,
        featureChoicesExtract: jod.product.getFeatureChoicesExtract(),
      };

      const buildEmbs = (qty: number): FlatEmb[] =>
        (jod.joDetailAddons ?? []).map((jodAddon) => ({
          description: jodAddon.addon.nameAndLocation,
          additionalInfo: jodAddon.additionalInfo,
          qty: qty,
          unitPrice: jodAddon.unitPriceAmt > 0 ? fmtCurrency(jodAddon.unitPriceAmt) : '',
          total: jodAddon.unitPriceAmt > 0 ? fmtCurrency(qty * (jodAddon.unitPriceAmt || 0)) : '',
        }));

      // Excludes mods — the add-on rows below the leg carry those.
      const unitTotalPreModsAmt = jod.unitTotalAmt - jod.unitModsAmt;

      // A detail with neither quantity still prints a New row so the line doesn't vanish.
      if (shipQty > 0 || reissueQty === 0) {
        this.flatLegs.push({
          ...identity,
          legLabel: 'New',
          qty: shipQty,
          unitAmt: fmtCurrency(jod.unitPriceAmt),
          unitOs: fmtCurrency(jod.unitOversizeAmt),
          total: fmtCurrency(unitTotalPreModsAmt * shipQty),
          embs: buildEmbs(shipQty),
        });
      }

      if (reissueQty > 0) {
        this.flatLegs.push({
          ...identity,
          legLabel: 'Reissue',
          qty: reissueQty,
          unitAmt: fmtCurrency(jod.unitReissueAmt),
          unitOs: '—',
          total: fmtCurrency((jod.unitReissueAmt || 0) * reissueQty),
          embs: chargeAddonsOnReissuance ? buildEmbs(reissueQty) : [],
        });
      }
    });

    const joHeader = this.invoiceHeader.joHeader;

    this.employee = (joHeader.empFirstName || "") + " " + (joHeader.empLastName || "") + " " + (joHeader.empEmail || "");

    this.serviceDisplayName = this.getServiceDisplayName(this.invoiceHeader) ?? "N/A";
    const boxes = this.invoiceHeader.shipment?.shippingBoxes;
    if (boxes && boxes.length > 0) {
      this.labelApi = boxes[0].trackingLabelApiIdentifier ?? "N/A";
    } else {
      this.labelApi = "N/A";
    }
  }

  /*
   * =====================================================================================
   * ORIGINAL ngOnInit() — COMMENTED OUT 2026-05-03 8:30AM
   *
   * This was the root cause of the bug. ngOnInit() only fires once per component instance.
   * When PuppeteerSharp's PageHandler reuses the same browser page and navigates via Angular's
   * in-app router (window.navigateByUrl in PageHandler.cs line 61), Angular reuses this
   * component instance for the next invoice. Since ngOnInit() doesn't fire again, the computed
   * fields (employee, flatDetails, serviceDisplayName, labelApi) retained stale values from
   * the first invoice. Replaced with ngOnChanges() + buildFromInvoice() above.
   * =====================================================================================
   *
   * ngOnInit() {
   *   const invoiceDetails = this.invoiceHeader.invoiceDetails;
   *
   *   const fmtCurrency = UtilFns.fmtCurrency;
   *   let modTotal = 0;
   *   this.flatDetails = invoiceDetails.map((d) => {
   *     const jod = d.joDetail;
   *     const style = jod.product.productType;
   *     const ptConfigName = jod.productTypeConfigName;
   *     const embs =
   *       jod.joDetailAddons &&
   *       jod.joDetailAddons.map((jodAddon) => {
   *         modTotal += d.shipQty * (jodAddon.unitPriceAmt || 0);
   *         return {
   *           description: jodAddon.addon.nameAndLocation,
   *           additionalInfo: jodAddon.additionalInfo,
   *
   *           unitPrice: jodAddon.unitPriceAmt > 0 ? fmtCurrency(jodAddon.unitPriceAmt) : "",
   *           total: jodAddon.unitPriceAmt > 0 ? fmtCurrency(d.shipQty * (jodAddon.unitPriceAmt || 0)) : "",
   *         };
   *       });
   *
   *     const unitTotalPreModsAmt = jod.unitTotalAmt - jod.unitModsAmt;
   *     return {
   *       mfr: style.manufacturer.name,
   *       desc: style.description,
   *       // style: style.style,
   *       ptConfigName: ptConfigName,
   *       featureChoicesExtract: jod.product.getFeatureChoicesExtract(),
   *       qty: d.shipQty,
   *       unitPrice: fmtCurrency(jod.unitPriceAmt),
   *       unitOs: fmtCurrency(jod.unitOversizeAmt),
   *       unitCus: fmtCurrency(jod.unitCustomAmt),
   *       reQty: d.redistributeQty,
   *       reAmt: fmtCurrency(jod.unitReissueAmt),
   *       total: fmtCurrency(unitTotalPreModsAmt * d.shipQty + ((d.redistributeQty || 0) * jod.unitReissueAmt || 0)),
   *       embs: embs,
   *     };
   *   });
   *   const joHeader = this.invoiceHeader.joHeader;
   *
   *   this.employee = (joHeader.empFirstName || "") + " " + (joHeader.empLastName || "") + " " + (joHeader.empEmail || "");
   *   this.modTotal = modTotal;
   *
   *   this.serviceDisplayName = this.getServiceDisplayName(this.invoiceHeader) ?? "N/A";
   *   const boxes = this.invoiceHeader.shipment?.shippingBoxes;
   *   if (boxes?.length > 0) {
   *     this.labelApi = boxes[0].trackingLabelApiIdentifier ?? "N/A";
   *   } else {
   *     this.labelApi = "N/A";
   *   }
   * }
   */

  getCarrierDisplayName(invoiceHeader: InvoiceHeader) {
    if (invoiceHeader?.shipment) {
      return this.shippingService.getCarrierDisplayName(invoiceHeader.shipment.carrierApiIdentifier);
    }
    return null;
  }

  getServiceDisplayName(invoiceHeader: InvoiceHeader) {
    if (invoiceHeader?.shipment) {
      return this.shippingService.getServiceDisplayName(
        invoiceHeader.shipment.carrierApiIdentifier,
        invoiceHeader.shipment.serviceApiIdentifier
      );
    }
    return null;
  }

  getBillingAddress() {
    if (this.isManifested) {
      return this.invoiceHeader.joHeader.billingName + "\n\n\n";
    }
    return UtilFns.getAddress(this.invoiceHeader.joHeader, "billing", false, false);
  }

  /** Visa XXXX1111 -> Visa 1111 */
  get paymentName() {
    const name = this.invoiceHeader.joHeader.pCardName;
    if (!name || !name.indexOf(" ")) {
      return name;
    }
    return name.replace("XXXX", "");
  }

  get isPosted() {
    return this.invoiceHeader && this.invoiceHeader.isPostedOrQueued;
  }

  get isManifested() {
    return this.invoiceHeader && this.invoiceHeader.joHeader?.manifestId;
  }

  get amountLabel() {
    return this.invoiceHeader.roundedTotalAmt() < 0 ? "Available Credit" : "Amount Due";
  }

  get totalAmtAbs() {
    return Math.abs(this.invoiceHeader.roundedTotalAmt());
  }

  get totalAmtOrZero() {
    const amt = this.invoiceHeader.roundedTotalAmt();
    return amt < 0 ? 0 : amt;
  }

  get programName(): string {
    return (this.invoiceHeader?.joHeader as any)?.programName ?? "";
  }

  get programUserGroupName(): string {
    return (this.invoiceHeader?.joHeader as any)?.programUserGroupName ?? "";
  }

  get shippedViaAndShippingId(): string {
    if (this.serviceDisplayName === "N/A" && this.labelApi === "N/A") return "N/A";
    return `${this.serviceDisplayName} / ${this.labelApi}`;
  }
}
